'use client';

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Dialog } from '@base-ui-components/react/dialog';
import { Button } from './button';
import { Icon } from './icon/icon';
import type { IconName } from './icon/icon-registry';
import { ModalProvider, useModal } from './modal';
import styles from './confirm-dialog.module.css';

/** Handed to a `body` render function, so its controls can gate the confirm button. */
export interface ConfirmBodyApi {
  setConfirmDisabled: (disabled: boolean) => void;
}

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  disableClose?: boolean;
  /**
   * Drops the Cancel button, leaving only the confirm one — for a notice the
   * user can only acknowledge ("Got it"), where Cancel would mean nothing.
   * The promise still resolves `false` on Escape/backdrop, as any close does.
   */
  hideCancel?: boolean;
  /**
   * `warning` (default) is the red alert triangle every are-you-sure prompt
   * wears. `info` is the blue icon container the other dialogs use, for a
   * confirm that is a choice rather than a risk — e.g. Duplicate.
   */
  tone?: 'warning' | 'info';
  /** The icon inside the tone's container. Defaults to `alert-triangle`. */
  icon?: IconName;
  /**
   * Form controls under the message — the options a confirm needs answered
   * before it can go ahead (what to copy, where to). Rendered full-width and
   * left-aligned inside the same chrome. The body owns its own state and
   * reports it to the caller however the caller likes; the promise still
   * resolves only `true`/`false`.
   *
   * A function receives `setConfirmDisabled`, for a body whose answers can be
   * incomplete (a required field left empty).
   */
  body?: ReactNode | ((api: ConfirmBodyApi) => ReactNode);
  /** The confirm button's disabled state until the body says otherwise. */
  confirmDisabled?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * `useConfirm` for a shared component that may render outside
 * `<ConfirmProvider>` (a bare unit test, the handbook) — `null` there instead
 * of throwing, so the caller can fall back.
 */
export function useOptionalConfirm(): ConfirmFn | null {
  return useContext(ConfirmContext);
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx;
}

// `confirm` is the confirm button's colour. A warning confirm is red, as every
// falcon-fe `ModalComponent` confirm is (`error` filled); an info confirm is a
// choice rather than a risk, so it keeps the primary blue.
const TONE_STYLE = {
  warning: { container: 'bg-[#FFE1E1]', icon: '#BE1717', confirm: 'error' },
  info: {
    container: 'bg-[var(--dialog-icon-container-background-default,#d6eeff)]',
    icon: 'var(--modal-icon-container-icon-default, #0062ff)',
    confirm: 'primary',
  },
} as const;

/**
 * The one chrome every confirm renders through. A component rather than
 * inline JSX so `body` can flip the confirm button's disabled state.
 */
function ConfirmDialogView({
  options,
  close,
}: Readonly<{ options: ConfirmOptions; close: (result: boolean) => void }>) {
  const [confirmDisabled, setConfirmDisabled] = useState(options.confirmDisabled ?? false);
  const tone = TONE_STYLE[options.tone ?? 'warning'];
  const body =
    typeof options.body === 'function' ? options.body({ setConfirmDisabled }) : options.body;

  // One chrome for every confirm, synced to falcon-fe's `ModalComponent`:
  // centered icon, centered regular-weight title and message, then Cancel and
  // confirm as two equal full-width halves. The confirm button's colour
  // follows `tone` — red for an are-you-sure, blue for a choice. There used to
  // be a second, bordered/X-close "destructive" chrome reserved for deletes;
  // that stays gone — a delete is told apart from any other warning confirm
  // only by its own title/message/confirmLabel copy.
  return (
    <div className="w-[calc(100%-2px)] min-w-0 flex flex-col items-center gap-2 text-center">
      <div className={`flex items-center justify-center rounded-full p-2 ${tone.container}`}>
        <Icon name={options.icon ?? 'alert-triangle'} size="md" color={tone.icon} />
      </div>
      <div className="flex flex-col gap-2">
        {/* falcon-fe's own type classes: 18px title and 14px message, both
            at weight 300 — the lighter read the message has there. */}
        <Dialog.Title className={`body-lg-regular ${styles.title}`}>{options.title}</Dialog.Title>
        {options.message ? (
          <p className={`body-sm-regular ${styles.message}`}>{options.message}</p>
        ) : null}
      </div>

      {body ? <div className="mt-2 flex w-full flex-col gap-3 text-left">{body}</div> : null}

      <div className="mt-6 flex w-full gap-2.5 [&>*]:flex-1">
        {options.hideCancel ? null : (
          <Button
            size="small"
            color="secondary"
            variant="outlined"
            onClick={() => close(false)}
          >
            {options.cancelLabel ?? 'Cancel'}
          </Button>
        )}
        <Button
          size="small"
          color={tone.confirm}
          variant="filled"
          data-modal-confirm
          disabled={confirmDisabled}
          onClick={() => close(true)}
        >
          {options.confirmLabel ?? 'Confirm'}
        </Button>
      </div>
    </div>
  );
}

function ConfirmProviderInner({ children }: { children: ReactNode }) {
  const openModal = useModal();

  const confirm = useCallback<ConfirmFn>(
    (options) => {
      return openModal<boolean>({
        disableClose: options.disableClose,
        defaultCloseResult: false,
        confirmAutofocus: true,
        panelClassName: styles.panel,
        // The chrome itself — see `ConfirmDialogView`'s comment.
        content: ({ close }) => <ConfirmDialogView options={options} close={close} />,
      }).then((value) => value ?? false);
    },
    [openModal],
  );

  return <ConfirmContext.Provider value={confirm}>{children}</ConfirmContext.Provider>;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  return (
    <ModalProvider>
      <ConfirmProviderInner>{children}</ConfirmProviderInner>
    </ModalProvider>
  );
}
